import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	SvgIcon,
	SvgIconProps,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Theme,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useId, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
	EdgerRollPass,
	EdgerRollPassResponse,
} from "@/pages/catalogs/models/EdgerRoll";
import {
	deleteEdgerRollPass,
	getEdgerRollPasses,
	upsertEdgerRollPass,
} from "@/pages/catalogs/repositories/EdgerRollRepository";
import { grey, yellow } from "@mui/material/colors";
import { PassesSectionsModal } from "./EditSectionsModal";

interface PassCircleProps extends SvgIconProps {
	firstColor: string;
	secondColor?: string;
	isDiagonal?: boolean;
}

export const PassCircle: React.FC<PassCircleProps> = ({
	firstColor,
	secondColor,
	isDiagonal = false,
	...props
}) => {
	const gradientId = useId();
	return (
		<SvgIcon {...props} viewBox="0 0 24 24">
			<defs>
				<linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
					<stop offset="50%" stopColor={firstColor} />
					<stop offset="50%" stopColor={secondColor ?? firstColor} />
				</linearGradient>
			</defs>
			<circle
				cx="12"
				cy="12"
				r="10"
				fill={`url(#${gradientId})`}
				style={{ fill: `url(#${gradientId})` }}
			/>
			{isDiagonal && (
				<line
					x1="5.5"
					y1="18.5"
					x2="18.5"
					y2="5.5"
					stroke="#fff"
					strokeWidth="3.5"
					strokeLinecap="round"
				/>
			)}
		</SvgIcon>
	);
};

export const getPassColor = (status: string, theme: Theme) => {
	if (status === "R") return theme.palette.success;
	if (status === "U") return { main: yellow[700], dark: yellow[900] };
	if (status == "D") return theme.palette.error;
	return { main: grey[400], dark: grey[600] };
};

export const parseEdgerRollPasses = (rows: EdgerRollPassResponse[]) => {
	const passMap = new Map<number, EdgerRollPass>();

	for (const row of rows) {
		if (!passMap.has(row.PassNumber)) {
			passMap.set(row.PassNumber, {
				PassNumber: row.PassNumber,
				PassStatusCode: row.PassStatusCode,
				IsDiagonal: !!row.IsDiagonal,
				StatusDescription: row.StatusDescription,
				Sections: [],
			});
		}

		if (row.SectionId != null && row.SectionName != null) {
			passMap.get(row.PassNumber)!.Sections.push({
				SectionId: row.SectionId,
				SectionName: row.SectionName,
			});
		}
	}

	return Array.from(passMap.values());
};

type usePassesProps = {
	partId: number;
	onError: (error: Error) => void;
	onSuccess?: (data: EdgerRollPassResponse[]) => void;
};

export const usePasses = ({ partId, onError, onSuccess }: usePassesProps) => {
	return useQuery<EdgerRollPassResponse[], Error>({
		queryKey: ["passes", partId],
		queryFn: ({ signal }) => {
			if (partId) return getEdgerRollPasses({ partId }, signal);
			return [];
		},
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	partId: number;
	mode?: "edit" | "create";
	disabled?: boolean;
};

export const PassesGrid = ({
	partId,
	mode = "create",
	disabled = false,
}: Props) => {
	const theme = useTheme();

	const [error, setError] = useState("");
	const [editingPass, setEditingPass] = useState<EdgerRollPass | null>(null);

	const queryClient = useQueryClient();

	const { data, isLoading } = usePasses({
		partId: partId,
		onError: (error) => setError(error.message),
	});

	const passes = parseEdgerRollPasses(data ?? []);

	const deleteMutation = useMutation(
		({ partId }: { partId: number }) => deleteEdgerRollPass({ partId }),
		{
			meta: { successMessage: "Last pass deleted successfully." },
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["passes"] });
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	const upsertMutation = useMutation(
		({
			status,
			passNumber,
			partId,
			sectionIds,
			isDiagonal,
			isDiagonalForAll,
		}: {
			status?: string;
			passNumber?: number;
			partId: number;
			sectionIds?: string;
			isDiagonal?: boolean;
			isDiagonalForAll?: boolean;
		}) =>
			upsertEdgerRollPass({
				status: status?.charAt(0),
				passNumber,
				partId,
				sectionIds,
				isDiagonal,
				isDiagonalForAll,
			}),
		{
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({ queryKey: ["passes"] });
				enqueueSnackbar(
					variables.isDiagonalForAll === undefined
						? "Pass updated successfully."
						: variables.isDiagonalForAll
							? "All passes marked as diagonal."
							: "All passes marked as not diagonal.",
					{ variant: "success" },
				);
				setEditingPass(null);
			},
			onError: (error: Error) => {
				setError(error.message);
			},
		},
	);

	const isAddDisabled = disabled || mode == "edit";
	const isDeleteDisabled =
		disabled || mode == "edit" || (passes ? passes.length <= 0 : false);

	const handleStatusClick = (pass: EdgerRollPass) => {
		if (disabled) return;

		const isReady = pass.PassStatusCode === "R";
		const isUsed = pass.PassStatusCode === "U";
		const isDamaged = pass.PassStatusCode === "D";
		const status = isReady ? "U" : isUsed ? "D" : "R";

		upsertMutation.mutate({
			status,
			passNumber: pass.PassNumber ?? 0,
			partId: partId ?? 0,
			isDiagonal: pass.IsDiagonal,
		});
	};

	const handleSaveSections = (selectedSectionIds: number[]) => {
		if (!editingPass) return;
		upsertMutation.mutate({
			status: editingPass.PassStatusCode,
			passNumber: editingPass.PassNumber ?? 0,
			partId: partId ?? 0,
			sectionIds: selectedSectionIds.join(","),
			isDiagonal: editingPass.IsDiagonal,
		});
	};

	const handleDiagonalChange = (pass: EdgerRollPass, isDiagonal: boolean) => {
		if (disabled) return;

		upsertMutation.mutate({
			status: pass.PassStatusCode,
			passNumber: pass.PassNumber ?? 0,
			partId: partId ?? 0,
			isDiagonal,
		});
	};

	const handleApplyDiagonalToAll = (isDiagonal: boolean) => {
		if (disabled) return;

		upsertMutation.mutate({
			partId: partId ?? 0,
			isDiagonalForAll: isDiagonal,
		});
	};

	const areAllPassesDiagonal =
		passes.length > 0 && passes.every((pass) => pass.IsDiagonal);
	const areSomePassesDiagonal = passes.some((pass) => pass.IsDiagonal);

	const headerCellSx = {
		backgroundColor: "#f8f8f8",
		borderBottom: 1,
		borderRight: 1,
		borderColor: "divider",
		padding: "10px 10px",
		fontSize: 11,
		fontWeight: 600,
		color: "#333",
		whiteSpace: "nowrap",
		lineHeight: 1.2,
		"&:last-child": { borderRight: "none" },
	};

	const bodyCellSx = {
		borderBottom: 1,
		borderRight: 1,
		borderColor: "divider",
		padding: "4px 10px",
		fontSize: 12,
		color: "#333",
		"&:last-child": { borderRight: "none" },
	};

	return (
		<>
			<Box
				sx={{
					height: "24vh",
					display: "flex",
					flexDirection: "column",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					overflow: "hidden",
					bgcolor: "background.paper",
				}}
			>
				<TableContainer
					sx={{
						flex: 1,
						overflow: "auto",
						"&::-webkit-scrollbar": { width: 6, height: 6 },
						"&::-webkit-scrollbar-track": { background: "#f1f1f1" },
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: 3,
							"&:hover": { background: "#a1a1a1" },
						},
					}}
				>
					{isLoading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
							}}
						>
							<CircularProgress size={20} />
						</Box>
					) : !passes || passes.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								color: "#666",
								fontSize: 12,
							}}
						>
							No passes to show
						</Box>
					) : (
						<Table
							stickyHeader
							size="small"
							sx={{ tableLayout: "fixed", width: "100%" }}
						>
							<TableHead>
								<TableRow sx={{ height: 46 }}>
									<TableCell width="8%" align="center" sx={headerCellSx} />
									<TableCell width="10%" sx={headerCellSx}>
										Pass
									</TableCell>
									<TableCell width="40%" sx={headerCellSx}>
										Sections
									</TableCell>
									<TableCell width="20%" align="center" sx={headerCellSx}>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 0.5,
												lineHeight: 1.2,
											}}
										>
											<Tooltip
												title={
													areAllPassesDiagonal
														? "Mark all passes as not diagonal"
														: "Mark all passes as diagonal"
												}
											>
												<span>
													<Checkbox
														size="small"
														color="primary"
														checked={areAllPassesDiagonal}
														indeterminate={
															areSomePassesDiagonal && !areAllPassesDiagonal
														}
														disabled={
															disabled ||
															passes.length === 0 ||
															upsertMutation.isLoading
														}
														onChange={(e) =>
															handleApplyDiagonalToAll(e.target.checked)
														}
														sx={{ padding: 0 }}
														data-test-id="apply-diagonal-to-all"
													/>
												</span>
											</Tooltip>
											Diagonal
										</Box>
									</TableCell>
									<TableCell width="25%" align="center" sx={headerCellSx}>
										Status
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{passes.map((pass) => {
									return (
										<TableRow
											key={`${pass.PassNumber}`}
											data-test-id={`pass-row-${pass.PassNumber}`}
											sx={{
												height: 32,
												"&:hover": { bgcolor: "#f5f7ff" },
												"&:nth-of-type(even)": {
													bgcolor: "#fcfcfc",
													"&:hover": { bgcolor: "#f5f7ff" },
												},
											}}
										>
											<TableCell
												sx={{
													...bodyCellSx,
													padding: 0,
												}}
											>
												<Tooltip
													title={`Click to toggle status to ${pass.StatusDescription === "Ready" ? "'Damaged'" : "'Ready'"}`}
												>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: "100%",
															height: "100%",
															minHeight: 30,
														}}
													>
														<PassCircle
															onClick={() =>
																!disabled && handleStatusClick(pass)
															}
															firstColor={
																getPassColor(pass.PassStatusCode, theme).main
															}
															isDiagonal={pass.IsDiagonal}
															sx={{
																fontSize: 20,
																display: "block",
																margin: "0 auto",
																padding: 0,
																lineHeight: 0,
																verticalAlign: "middle",
																cursor: disabled ? "default" : "pointer",
																transition:
																	"transform 0.1s ease, box-shadow 0.1s ease",
																"&:hover": disabled
																	? {}
																	: {
																			transform: "scale(1.1)",
																			boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
																		},
																"&:active": disabled
																	? {}
																	: { transform: "scale(0.95)" },
															}}
														/>
													</Box>
												</Tooltip>
											</TableCell>
											<TableCell sx={bodyCellSx}>{pass.PassNumber}</TableCell>
											<TableCell sx={bodyCellSx}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														width: "100%",
														gap: 1,
													}}
												>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														{pass.Sections.length > 0 ? (
															<Typography
																variant="body2"
																sx={{
																	fontSize: 11,
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																}}
															>
																{pass.Sections.map((s) => s.SectionName).join(
																	", ",
																)}
															</Typography>
														) : (
															<Typography
																variant="body2"
																color="textSecondary"
																sx={{ fontSize: 11, fontStyle: "italic" }}
															>
																No sections assigned
															</Typography>
														)}
													</Box>
													{!disabled && (
														<Tooltip title="Edit Sections">
															<IconButton
																size="small"
																onClick={(e) => {
																	if (disabled) return;
																	setEditingPass(pass);
																}}
																sx={{ padding: 0, flexShrink: 0 }}
																data-test-id="edit-pass"
															>
																<EditIcon sx={{ fontSize: 14 }} />
															</IconButton>
														</Tooltip>
													)}
												</Box>
											</TableCell>
											<TableCell
												align="center"
												sx={{ ...bodyCellSx, padding: 0 }}
											>
												<Checkbox
													size="small"
													color="primary"
													checked={pass.IsDiagonal}
													disabled={disabled}
													onChange={(e) =>
														handleDiagonalChange(pass, e.target.checked)
													}
													sx={{ padding: 0 }}
													data-test-id="pass-diagonal"
												/>
											</TableCell>
											<TableCell align="center" sx={bodyCellSx}>
												<Tooltip
													title={`Click to toggle status to ${pass.StatusDescription === "Ready" ? "'Damaged'" : "'Ready'"}`}
												>
													<Chip
														label={pass.StatusDescription}
														onClick={() => !disabled && handleStatusClick(pass)}
														size="small"
														sx={{
															height: 20,
															fontSize: 10,
															fontWeight: 600,
															transition:
																"transform 0.1s ease, box-shadow 0.1s ease",
															"&:hover": disabled
																? {}
																: {
																		transform: "scale(1.1)",
																		boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
																	},
															"&:active": disabled
																? {}
																: { transform: "scale(0.95)" },
															bgcolor: (theme) =>
																alpha(
																	getPassColor(pass.PassStatusCode, theme).main,
																	0.12,
																),
															color: (theme) =>
																getPassColor(pass.PassStatusCode, theme).dark,
															border: (theme) =>
																`1px solid ${
																	getPassColor(pass.PassStatusCode, theme).main
																},
                                  0.3,
                                )}`,
															"& .MuiChip-label": { padding: "0 6px" },
														}}
														data-test-id="pass-status"
													/>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</TableContainer>

				{!disabled && mode == "create" && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "4px 10px",
							borderTop: 1,
							borderColor: "divider",
							bgcolor: "#f8f8f8",
							minHeight: 30,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<Box sx={{ display: "flex", gap: 0.4 }}>
								{passes?.map((i) => (
									<Box
										key={i.PassNumber}
										sx={{
											width: 5,
											height: 5,
											borderRadius: "50%",
											bgcolor: (theme) =>
												getPassColor(i.PassStatusCode, theme).main,
										}}
									/>
								))}
							</Box>
						</Box>
						<Box sx={{ display: "flex", gap: 0.5 }}>
							<Tooltip title={"Add Pass"}>
								<span>
									<IconButton
										color="primary"
										onClick={() =>
											upsertMutation.mutate({ partId, isDiagonal: false })
										}
										disabled={isAddDisabled}
										size="small"
										sx={{
											padding: "3px",
											borderRadius: "2px",
											"&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
											"& .MuiSvgIcon-root": { fontSize: 16 },
										}}
									>
										<AddIcon />
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip
								title={
									isDeleteDisabled ? "No passes to delete" : "Delete Last Pass"
								}
							>
								<span>
									<IconButton
										color="error"
										onClick={() => deleteMutation.mutate({ partId })}
										disabled={isDeleteDisabled || deleteMutation.isLoading}
										size="small"
										sx={{
											padding: "3px",
											borderRadius: "2px",
											"&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
											"& .MuiSvgIcon-root": { fontSize: 16 },
										}}
									>
										{deleteMutation.isLoading ? (
											<CircularProgress size={12} color="inherit" />
										) : (
											<DeleteIcon />
										)}
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					</Box>
				)}
			</Box>

			<PassesSectionsModal
				open={!!editingPass}
				onClose={() => setEditingPass(null)}
				onSave={handleSaveSections}
				currentSectionIds={
					editingPass?.Sections.map((s) => s.SectionId).join(",") ?? null
				}
				passNumber={editingPass?.PassNumber ?? null}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
